import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Ban, CalendarClock, CheckCheck, CircleX, FileText, Globe, Users } from "lucide-react";
import type { CampaignStatusName } from "@multi-chat/shared";
import { ChannelIcon } from "../../components/channel/ChannelIcon.js";
import { LtrValue } from "../../components/common/LtrValue.js";
import { cn } from "../../lib/utils.js";
import { campaignShellStatusLabelKey } from "./campaign-draft-workflow.js";
import { CampaignProcessingIcon } from "./CampaignProcessingIcon.js";

export type CampaignPostLaunchOverviewStatus = Exclude<CampaignStatusName, "DRAFT">;

function OverviewStatusIcon({
  status,
}: {
  status: CampaignPostLaunchOverviewStatus;
}): React.JSX.Element {
  switch (status) {
    case "SCHEDULED":
      return <CalendarClock size={18} aria-hidden="true" />;
    case "RUNNING":
      return <CampaignProcessingIcon size={16} />;
    case "COMPLETED":
      return <CheckCheck size={18} aria-hidden="true" />;
    case "FAILED":
      return <CircleX size={18} aria-hidden="true" />;
    case "CANCELLED":
      return <Ban size={18} aria-hidden="true" />;
    default: {
      const _exhaustive: never = status;
      return _exhaustive;
    }
  }
}

function overviewIconToneClass(status: CampaignPostLaunchOverviewStatus): string {
  switch (status) {
    case "SCHEDULED":
      return "text-foreground";
    case "RUNNING":
      return "text-accent";
    case "COMPLETED":
      return "text-success";
    case "FAILED":
      return "text-error";
    case "CANCELLED":
      return "text-muted";
    default: {
      const _exhaustive: never = status;
      return _exhaustive;
    }
  }
}

/** Running shows only its header; the results panel below carries the important information. */
function overviewShowsSummary(status: CampaignPostLaunchOverviewStatus): boolean {
  switch (status) {
    case "RUNNING":
      return false;
    case "SCHEDULED":
    case "COMPLETED":
    case "FAILED":
    case "CANCELLED":
      return true;
    default: {
      const _exhaustive: never = status;
      return _exhaustive;
    }
  }
}

const valueClass = "m-0 max-w-full text-[0.875rem] font-semibold leading-snug text-foreground";
const secondaryClass = "m-0 max-w-full text-[0.75rem] leading-snug text-muted";

function SummaryCell({
  label,
  testId,
  className,
  children,
}: {
  label: string;
  testId: string;
  className?: string;
  children: ReactNode;
}): React.JSX.Element {
  return (
    <div className={cn("grid min-w-0 content-start gap-1 bg-surface px-4 py-3", className)} data-testid={testId}>
      <dt className="text-[0.75rem] font-medium text-muted">{label}</dt>
      <dd className="m-0 grid min-w-0 justify-items-start gap-0.5">{children}</dd>
    </div>
  );
}

export function CampaignPostLaunchOverview({
  status,
  subtitle,
  recipientCount,
  showSender,
  senderName,
  senderPhone,
  templateName,
  sendTime,
  timezone,
  actions,
  children,
}: {
  status: CampaignPostLaunchOverviewStatus;
  subtitle: string | null;
  recipientCount: number;
  /** The sender is only known for scheduled campaigns; other states omit the cell. */
  showSender: boolean;
  senderName: string | null;
  senderPhone: string | null;
  templateName: string | null;
  sendTime: { date: string; time: string } | null;
  timezone: string | null;
  actions?: ReactNode;
  children?: ReactNode;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const cellCount = showSender ? 4 : 3;

  if (status === "SCHEDULED") {
    return (
      <ScheduledOverview
        subtitle={subtitle}
        recipientCount={recipientCount}
        senderName={senderName}
        senderPhone={senderPhone}
        templateName={templateName}
        sendTime={sendTime}
        timezone={timezone}
        actions={actions}
      >
        {children}
      </ScheduledOverview>
    );
  }

  return (
    <section
      className="grid gap-4"
      data-testid="campaign-post-launch-overview"
      data-status={status}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-3">
          <span
            className={cn(
              "grid size-10 shrink-0 place-items-center rounded-lg border border-line/70 bg-surface-muted",
              overviewIconToneClass(status),
            )}
            aria-hidden="true"
            data-testid="campaign-post-launch-status-icon"
          >
            <OverviewStatusIcon status={status} />
          </span>
          <div className="grid min-w-0 gap-0.5 pt-0.5">
            <h2 className="m-0 text-[1rem] font-semibold leading-snug tracking-[-0.01em] text-foreground">
              {t(campaignShellStatusLabelKey(status))}
            </h2>
            {subtitle ? (
              <p className="m-0 text-[0.8125rem] text-muted" data-testid="campaign-post-launch-subtitle">
                {subtitle}
              </p>
            ) : null}
          </div>
        </div>
        {actions ? (
          <div className="flex flex-wrap items-center gap-2 max-sm:w-full max-sm:[&>*]:flex-1">
            {actions}
          </div>
        ) : null}
      </div>

      {overviewShowsSummary(status) ? (
        <dl
          className={cn(
            "m-0 grid gap-px overflow-hidden rounded-lg border border-line/70 bg-line/60 sm:grid-cols-2",
            cellCount === 4 ? "lg:grid-cols-4" : "lg:grid-cols-3",
          )}
          data-testid="campaign-post-launch-summary"
        >
          <SummaryCell label={t("campaigns.review.checklist.audience")} testId="campaign-post-launch-audience">
            <p className={cn(valueClass, "tabular-nums")}>
              {new Intl.NumberFormat(i18n.language).format(recipientCount)}
            </p>
          </SummaryCell>

          {showSender ? (
            <SummaryCell label={t("campaigns.review.checklist.sender")} testId="campaign-post-launch-sender">
              <bdi className={cn(valueClass, "truncate")}>{senderName ?? "—"}</bdi>
              {senderPhone ? (
                <LtrValue className={cn(secondaryClass, "tabular-nums")}>{senderPhone}</LtrValue>
              ) : null}
            </SummaryCell>
          ) : null}

          <SummaryCell label={t("campaigns.review.checklist.template")} testId="campaign-post-launch-template">
            <bdi className={cn(valueClass, "break-all")}>{templateName ?? "—"}</bdi>
          </SummaryCell>

          <SummaryCell
            label={t("campaigns.review.checklist.delivery")}
            testId="campaign-post-launch-delivery"
            className={cellCount === 3 ? "sm:col-span-2 lg:col-span-1" : undefined}
          >
            {sendTime ? (
              <>
                <p className={valueClass}>
                  {sendTime.date}
                  <span className="font-normal text-muted" aria-hidden="true">
                    {" · "}
                  </span>
                  <LtrValue className="tabular-nums">{sendTime.time}</LtrValue>
                </p>
                {timezone ? <LtrValue className={secondaryClass}>{timezone}</LtrValue> : null}
              </>
            ) : (
              <p className={valueClass}>—</p>
            )}
          </SummaryCell>
        </dl>
      ) : null}

      {children}
    </section>
  );
}

function ScheduledDetail({
  icon,
  iconToneClass,
  label,
  testId,
  children,
}: {
  icon: ReactNode;
  iconToneClass: string;
  label: string;
  testId: string;
  children: ReactNode;
}): React.JSX.Element {
  return (
    <div className="flex min-w-0 items-start gap-3 px-5 py-4" data-testid={testId}>
      <span
        className={cn("grid size-9 shrink-0 place-items-center rounded-lg", iconToneClass)}
        aria-hidden="true"
      >
        {icon}
      </span>
      <div className="grid min-w-0 gap-0.5 pt-px">
        <dt className="text-[0.75rem] font-medium text-muted">{label}</dt>
        <dd className="m-0 grid min-w-0 justify-items-start gap-0.5">{children}</dd>
      </div>
    </div>
  );
}

function ScheduledOverview({
  subtitle,
  recipientCount,
  senderName,
  senderPhone,
  templateName,
  sendTime,
  timezone,
  actions,
  children,
}: {
  subtitle: string | null;
  recipientCount: number;
  senderName: string | null;
  senderPhone: string | null;
  templateName: string | null;
  sendTime: { date: string; time: string } | null;
  timezone: string | null;
  actions?: ReactNode;
  children?: ReactNode;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();

  return (
    <section
      className="@container overflow-hidden rounded-xl border border-line/70 bg-surface shadow-[0_1px_2px_rgba(15,23,42,0.04)]"
      data-testid="campaign-post-launch-overview"
      data-status="SCHEDULED"
    >
      <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-4 px-5 py-5">
        <div className="flex min-w-0 items-center gap-4">
          <span
            className="grid size-11 shrink-0 place-items-center rounded-xl border border-accent/15 bg-accent/10 text-accent"
            aria-hidden="true"
            data-testid="campaign-post-launch-status-icon"
          >
            <CalendarClock size={20} aria-hidden="true" />
          </span>
          <div className="grid min-w-0 gap-1.5">
            <h2 className="m-0 text-[1.0625rem] font-semibold leading-tight tracking-[-0.015em] text-foreground">
              {t(campaignShellStatusLabelKey("SCHEDULED"))}
            </h2>
            {sendTime ? (
              <p
                className="m-0 flex flex-wrap items-center gap-1.5"
                data-testid="campaign-post-launch-subtitle"
              >
                <span className="inline-flex items-center gap-1.5 rounded-md bg-accent/10 px-2.5 py-1 text-[0.8125rem] font-semibold leading-none text-foreground">
                  <span>{sendTime.date}</span>
                  <span className="font-normal text-accent/60" aria-hidden="true">
                    ·
                  </span>
                  <LtrValue className="tabular-nums">{sendTime.time}</LtrValue>
                </span>
                {timezone ? (
                  <LtrValue className="inline-flex items-center gap-1 rounded-md border border-line/70 bg-surface-muted/70 px-2 py-1 text-[0.6875rem] font-medium leading-none text-muted">
                    <Globe size={11} aria-hidden="true" />
                    {timezone}
                  </LtrValue>
                ) : null}
              </p>
            ) : subtitle ? (
              <p className="m-0 text-[0.8125rem] text-muted" data-testid="campaign-post-launch-subtitle">
                {subtitle}
              </p>
            ) : null}
          </div>
        </div>
        {actions ? (
          <div className="flex flex-wrap items-center gap-2 max-sm:w-full max-sm:[&>*]:flex-1">
            {actions}
          </div>
        ) : null}
      </div>

      <dl
        className="m-0 grid divide-y divide-line/70 border-t border-line/70 @2xl:grid-cols-3 @2xl:divide-x @2xl:divide-y-0"
        data-testid="campaign-post-launch-summary"
      >
        <ScheduledDetail
          icon={<Users size={16} />}
          iconToneClass="bg-accent/10 text-accent"
          label={t("campaigns.review.checklist.audience")}
          testId="campaign-post-launch-audience"
        >
          <p className={cn(valueClass, "tabular-nums")}>
            {new Intl.NumberFormat(i18n.language).format(recipientCount)}
          </p>
        </ScheduledDetail>
        <ScheduledDetail
          icon={<ChannelIcon platformType="WHATSAPP" size={16} />}
          iconToneClass="bg-whatsapp-muted text-whatsapp"
          label={t("campaigns.review.checklist.sender")}
          testId="campaign-post-launch-sender"
        >
          <bdi className={cn(valueClass, "break-words")}>{senderName ?? "—"}</bdi>
          {senderPhone ? (
            <LtrValue className={cn(secondaryClass, "tabular-nums")}>{senderPhone}</LtrValue>
          ) : null}
        </ScheduledDetail>
        <ScheduledDetail
          icon={<FileText size={16} />}
          iconToneClass="bg-primary/[0.07] text-primary/80"
          label={t("campaigns.review.checklist.template")}
          testId="campaign-post-launch-template"
        >
          <bdi className={cn(valueClass, "break-all")}>{templateName ?? "—"}</bdi>
        </ScheduledDetail>
      </dl>

      {children ? (
        <div
          className="grid gap-1.5 border-t border-line/70 bg-surface-muted/40 px-5 py-3"
          data-testid="campaign-scheduled-note"
        >
          {children}
        </div>
      ) : null}
    </section>
  );
}
